import styles from "./styles.module.scss";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { getColumnUserAccessHistory } from "../../helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import { Card, CardContent, Stack } from "@altimateai/lego";
import { useMemo, useState } from "react";
import { DataTable } from "../../components/tables";
import type { SortDirection } from "../../components/tables/types";
import type { ColumnAccessHistoryItem } from "./types";
import { UsagePercentageBar } from "./UsagePercentageBar";

type ColumnInfo = ColumnAccessHistoryItem["column"];
type UserAccess = { user_name: string; usage: number };
type SortableUserField = keyof UserAccess;

const GET_USER_ACCESS_HISTORY = "GET-USER-ACCESS-HISTORY";

const ColumnsAccessedSidebar = ({ columnObj }: { columnObj: ColumnInfo }) => {
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: [GET_USER_ACCESS_HISTORY, columnObj.rk],
    queryFn: () => getColumnUserAccessHistory({ column_rk: columnObj.rk }),
  });
  // Local state for sorting — this sidebar does client-side sorting on a small
  // dataset and shares the URL with the parent table, so URL params would collide.
  const [sortBy, setSortBy] = useState<SortableUserField>("user_name");
  const [sortOrder, setSortOrder] = useState<SortDirection>("asc");

  const sortedUsers = useMemo(() => {
    const users = usersData?.user_access ?? [];
    if (!sortBy) return users;
    return [...users].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      if (sortOrder === "asc") {
        if (typeof aValue === "number") return aValue - (bValue as number);
        return (aValue as string).localeCompare(bValue as string);
      } else {
        if (typeof aValue === "number") return (bValue as number) - aValue;
        return (bValue as string).localeCompare(aValue as string);
      }
    });
  }, [usersData, sortBy, sortOrder]);

  if (isUsersLoading) return <RelativeComponentLoader label="Loading content" />;
  return (
    <div>
      <Stack align="center" className="al-mb-2" direction="row">
        <Stack>
          <div className="al-text-base al-font-medium al-mr-3">User-Centric Column Utilization</div>
          <div className="al-text-lg al-font-light al-mr-3 al-text-brand">{columnObj.name}</div>
        </Stack>
        <div className="spacer" />
        <Card className={cn("al-mr-3 al-mb-0 no-break", styles.query_count)}>
          <CardContent className="al-pt-4 al-pb-2 al-pl-4 al-pr-4">
            <Stack direction="row" align="center" justify="between">
              <div>Queries: {usersData?.num_queries}</div>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <DataTable
        header={[
          {
            id: 1,
            label: "User Name",
            uid: "user_name",
            width: "37%",
            sort: {
              value: sortBy === "user_name" ? sortOrder : "",
              onChange: (direction: SortDirection) => {
                setSortBy("user_name");
                setSortOrder(direction || "asc");
              },
            },
            render: (item: UserAccess) => (
              <div className="al-text-brand al-break-all al-cursor-pointer">{item.user_name}</div>
            ),
          },
          {
            id: 2,
            label: "Column Accessed %",
            uid: "usage",
            width: "63%",
            sort: {
              value: sortBy === "usage" ? sortOrder : "",
              onChange: (direction: SortDirection) => {
                setSortBy("usage");
                setSortOrder(direction || "asc");
              },
            },
            render: (item: UserAccess) => (
              <div className="al-cursor-pointer">
                <UsagePercentageBar usagePercentage={item.usage} />
              </div>
            ),
          },
        ]}
        data={{ items: sortedUsers }}
        onRowClick={() => {}}
        idKey={(item: UserAccess) => columnObj.rk + item.user_name}
        minWidth={400}
      />
    </div>
  );
};

export default ColumnsAccessedSidebar;
